/**
 * Snapshot Stats Hook
 *
 * Cheap aggregate info for the active Scenario Editor snapshot: total /
 * edited / original counts, the data's date span, and which connected
 * accounts have no rows in the snapshot yet.
 *
 * Replaces SnapshotPage's previous pattern of calling
 * `useSnapshotTransactions` unfiltered and deriving all of this from the
 * *entire* row array (`snapshotData.length`, `.filter(t => t.is_modified)`,
 * a manual min/max date loop) — at 50k rows that was a full-dataset fetch
 * spent purely on producing five numbers and a list of account ids.
 *
 * @fileoverview React hook for Scenario Editor header stats
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { useAuth } from './useAuth';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

export interface SnapshotStats {
    total: number;
    editedCount: number;
    originalCount: number;
    minDate: string | null;
    maxDate: string | null;
    accountIdsPresent: string[];
}

const EMPTY_STATS: SnapshotStats = {
    total: 0,
    editedCount: 0,
    originalCount: 0,
    minDate: null,
    maxDate: null,
    accountIdsPresent: [],
};

export const useSnapshotStats = (tenantId?: string) => {
    const { session } = useAuth();
    const sessionRef = useRef(session);
    useEffect(() => { sessionRef.current = session; });
    const toast = useToast();

    const [stats, setStats] = useState<SnapshotStats>(EMPTY_STATS);
    const [loading, setLoading] = useState(false);

    const getHeaders = useCallback(() => {
        if (!sessionRef.current?.access_token || !tenantId) return null;
        return { headers: { 'x-tenant-id': tenantId } };
    }, [tenantId]);

    const fetchStats = useCallback(async (showErrorToast = false) => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        setLoading(true);
        try {
            const result = await apiJson<SnapshotStats>(
                '/api/transactions/snapshot/stats',
                { method: 'GET', ...config },
                sessionRef.current!.access_token,
            );
            setStats(result);
            return result;
        } catch (error) {
            console.error('Failed to fetch snapshot stats', error);
            if (showErrorToast) {
                toast({
                    title: 'Error loading scenario stats',
                    description: toUserMessage(error, 'Please try again in a moment.'),
                    status: 'error',
                    duration: 6000,
                    isClosable: true,
                });
            }
        } finally {
            setLoading(false);
        }
    }, [getHeaders, toast]);

    const initSnapshot = useCallback(async () => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        setLoading(true);
        try {
            await apiJson(
                '/api/transactions/snapshot/init',
                { method: 'POST', ...config },
                sessionRef.current!.access_token,
            );
            toast({ title: 'Snapshot initialized', status: 'success', duration: 2000 });
            return await fetchStats(true);
        } catch (error) {
            console.error('Failed to init snapshot', error);
            toast({ title: 'Failed to initialize snapshot', status: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getHeaders, toast, fetchStats]);

    const resetSnapshot = useCallback(async () => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        if (!window.confirm('Are you sure you want to reset the snapshot? All manual edits will be lost.')) {
            return;
        }

        setLoading(true);
        try {
            await apiJson(
                '/api/transactions/snapshot/reset',
                { method: 'POST', ...config },
                sessionRef.current!.access_token,
            );
            toast({ title: 'Snapshot reset', status: 'info', duration: 2000 });
            return await fetchStats(true);
        } catch (error) {
            console.error('Failed to reset snapshot', error);
            toast({ title: 'Failed to reset snapshot', status: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getHeaders, toast, fetchStats]);

    const refreshSnapshot = useCallback(async () => {
        const config = getHeaders();
        if (!config || !sessionRef.current?.access_token) return;

        setLoading(true);
        try {
            const result = await apiJson<{ status: string; added: number }>(
                '/api/transactions/snapshot/refresh',
                { method: 'POST', ...config },
                sessionRef.current!.access_token,
            );
            const added = result?.added ?? 0;
            toast({
                title: added > 0
                    ? `Added ${added} new transaction${added === 1 ? '' : 's'}`
                    : 'Scenario already up to date',
                status: added > 0 ? 'success' : 'info',
                duration: 2500,
            });
            return await fetchStats(true);
        } catch (error) {
            console.error('Failed to refresh snapshot', error);
            toast({ title: 'Failed to refresh scenario', status: 'error' });
        } finally {
            setLoading(false);
        }
    }, [getHeaders, toast, fetchStats]);

    return {
        stats,
        loading,
        fetchStats,
        initSnapshot,
        resetSnapshot,
        refreshSnapshot,
    };
};

export default useSnapshotStats;
